import { useState, useEffect, useRef, useMemo } from "react";
import classNames from "classnames";
import { AsYouType, getExampleNumber } from "libphonenumber-js";
import examples from "libphonenumber-js/mobile/examples";
import { useTranslation } from "react-i18next";

export const COUNTRY_PREFIXES = [
  { code: "+1", iso: "US", flag: "\u{1F1FA}\u{1F1F8}", name: "United States" },
  { code: "+44", iso: "GB", flag: "\u{1F1EC}\u{1F1E7}", name: "United Kingdom" },
  { code: "+39", iso: "IT", flag: "\u{1F1EE}\u{1F1F9}", name: "Italia" },
  { code: "+33", iso: "FR", flag: "\u{1F1EB}\u{1F1F7}", name: "France" },
  { code: "+34", iso: "ES", flag: "\u{1F1EA}\u{1F1F8}", name: "Espa\u00f1a" },
  { code: "+49", iso: "DE", flag: "\u{1F1E9}\u{1F1EA}", name: "Deutschland" },
  { code: "+31", iso: "NL", flag: "\u{1F1F3}\u{1F1F1}", name: "Nederland" },
  { code: "+32", iso: "BE", flag: "\u{1F1E7}\u{1F1EA}", name: "Belgium" },
  { code: "+41", iso: "CH", flag: "\u{1F1E8}\u{1F1ED}", name: "Switzerland" },
  { code: "+43", iso: "AT", flag: "\u{1F1E6}\u{1F1F9}", name: "\u00d6sterreich" },
  { code: "+351", iso: "PT", flag: "\u{1F1F5}\u{1F1F9}", name: "Portugal" },
  { code: "+55", iso: "BR", flag: "\u{1F1E7}\u{1F1F7}", name: "Brasil" },
  { code: "+52", iso: "MX", flag: "\u{1F1F2}\u{1F1FD}", name: "M\u00e9xico" },
  { code: "+54", iso: "AR", flag: "\u{1F1E6}\u{1F1F7}", name: "Argentina" },
  { code: "+57", iso: "CO", flag: "\u{1F1E8}\u{1F1F4}", name: "Colombia" },
  { code: "+61", iso: "AU", flag: "\u{1F1E6}\u{1F1FA}", name: "Australia" },
  { code: "+64", iso: "NZ", flag: "\u{1F1F3}\u{1F1FF}", name: "New Zealand" },
  { code: "+81", iso: "JP", flag: "\u{1F1EF}\u{1F1F5}", name: "Japan" },
  { code: "+82", iso: "KR", flag: "\u{1F1F0}\u{1F1F7}", name: "Korea" },
  { code: "+86", iso: "CN", flag: "\u{1F1E8}\u{1F1F3}", name: "China" },
  { code: "+91", iso: "IN", flag: "\u{1F1EE}\u{1F1F3}", name: "India" },
  { code: "+971", iso: "AE", flag: "\u{1F1E6}\u{1F1EA}", name: "UAE" },
  { code: "+972", iso: "IL", flag: "\u{1F1EE}\u{1F1F1}", name: "Israel" },
  { code: "+27", iso: "ZA", flag: "\u{1F1FF}\u{1F1E6}", name: "South Africa" },
];

// Keep label for backward compat
COUNTRY_PREFIXES.forEach((p) => {
  p.label = `${p.code}\u00a0 ${p.flag} ${p.name}`;
});

/**
 * Parse a raw E.164 phone string into { countryCode, localDigits }.
 *
 * Coerces `raw` to a string up front so callers can hand in legacy values
 * that aren't strict strings — some org records (e.g. profile.contactPhone)
 * came back from the brain as numbers, and `null` from the API skips the
 * default param. Without this, `raw.replace` crashes the whole panel
 * (caught by the Settings error boundary as "Error loading the content").
 */
export function parsePhone(raw = "") {
  const safe = raw == null ? "" : String(raw);
  const digits = safe.replace(/\D/g, "");
  if (safe.trim().startsWith("+")) {
    const sorted = [...COUNTRY_PREFIXES].sort((a, b) => b.code.length - a.code.length);
    for (const { code } of sorted) {
      const codeDigits = code.replace(/\D/g, "");
      if (digits.startsWith(codeDigits)) {
        return { countryCode: code, localDigits: digits.slice(codeDigits.length) };
      }
    }
  }
  // No + prefix — still strip a leading 1 if the number is 11 digits (country
  // code baked in without the +, e.g. "12025551234" stored from a legacy form).
  if (digits.length === 11 && digits.startsWith("1")) {
    return { countryCode: "+1", localDigits: digits.slice(1) };
  }
  return { countryCode: "+1", localDigits: digits };
}

function getMaxDigits(iso) {
  try {
    const example = getExampleNumber(iso, examples);
    if (example) return example.nationalNumber.length;
  } catch {
    // fall through
  }
  return 15;
}

function formatNational(digits, iso) {
  if (!digits) return "";
  // AsYouType("US") treats a leading "1" in the digit string as the NANP
  // country code prefix, formatting "1343434343" as "1 (343) 434-343" instead
  // of "(134) 343-4343". Since the country code is already shown separately in
  // the dropdown, use manual (XXX) XXX-XXXX formatting for US to avoid the
  // ambiguity entirely.
  if (iso === "US") {
    if (digits.length <= 3) return `(${digits}`;
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  const formatter = new AsYouType(iso);
  return formatter.input(digits) || digits;
}

/**
 * Phone input with country prefix dropdown and auto-formatting.
 * value / onChange work with E.164 format (e.g. "+12025551234").
 */
export default function PhoneInput({ value = "", onChange, disabled, placeholder, className }) {
  const { t } = useTranslation();
  const { countryCode, localDigits } = parsePhone(value);
  const currentPrefix = COUNTRY_PREFIXES.find((c) => c.code === countryCode) || COUNTRY_PREFIXES[0];
  const [open, setOpen] = useState(false);
  const [incomplete, setIncomplete] = useState(false);
  const [shaking, setShaking] = useState(false);
  const wrapperRef = useRef(null);

  const maxDigits = useMemo(() => getMaxDigits(currentPrefix.iso), [currentPrefix.iso]);
  const displayValue = useMemo(() => formatNational(localDigits, currentPrefix.iso), [localDigits, currentPrefix.iso]);

  useEffect(() => {
    if (localDigits.length >= maxDigits || localDigits.length === 0) setIncomplete(false);
  }, [localDigits.length, maxDigits]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const emitChange = (code, digits) => {
    onChange?.({ target: { value: `${code}${digits}` } });
  };

  const handleSelect = (prefix) => {
    const max = getMaxDigits(prefix.iso);
    const capped = localDigits.slice(0, max);
    emitChange(prefix.code, capped);
    setIncomplete(false);
    setOpen(false);
  };

  const handleBlur = (e) => {
    if (wrapperRef.current && wrapperRef.current.contains(e.relatedTarget)) return;
    if (localDigits.length > 0 && localDigits.length < maxDigits) {
      setIncomplete(true);
      setShaking(true);
    }
  };

  const handleDigitsChange = (e) => {
    const rawNew = e.target.value;
    let digits = rawNew.replace(/\D/g, "").slice(0, maxDigits);
    // Backspacing a separator shrinks the raw value but not the digit count;
    // the formatter would re-insert it. Pop a digit so backspace progresses.
    if (rawNew.length < displayValue.length && digits.length === localDigits.length) {
      digits = digits.slice(0, -1);
    }
    emitChange(countryCode, digits);
  };

  const placeholderText = useMemo(() => {
    try {
      const example = getExampleNumber(currentPrefix.iso, examples);
      if (example) return example.formatNational();
    } catch {
      // fall through
    }
    return placeholder || t("common.phoneNumber");
  }, [currentPrefix.iso, placeholder, t]);

  return (
    <div
      className={classNames("phone-intl-wrapper", { "phone-input-shake": shaking })}
      ref={wrapperRef}
      onAnimationEnd={() => setShaking(false)}
    >
      <div className="phone-prefix-picker">
        <button type="button" className="phone-prefix-btn" onClick={() => !disabled && setOpen(!open)} disabled={disabled}>
          <span className="phone-prefix-flag">{currentPrefix.flag}</span>
          <span className="phone-prefix-code">{currentPrefix.code}</span>
          <i className="notranslate material-icons phone-prefix-arrow">{open ? "expand_less" : "expand_more"}</i>
        </button>
        {open && (
          <ul className="phone-prefix-dropdown">
            {COUNTRY_PREFIXES.map((p) => (
              <li key={p.code} className={p.code === countryCode ? "active" : ""} onClick={() => handleSelect(p)}>
                <span className="phone-prefix-dropdown-flag">{p.flag}</span>
                <span className="phone-prefix-dropdown-code">{p.code}</span>
                <span className="phone-prefix-dropdown-name">{p.name}</span>
              </li>
            ))}
          </ul>
        )}
      </div>
      <input
        type="tel"
        value={displayValue}
        onChange={handleDigitsChange}
        onBlur={handleBlur}
        placeholder={placeholderText}
        disabled={disabled}
        className={classNames(className, { "phone-input-invalid": incomplete })}
        inputMode="tel"
        aria-invalid={incomplete}
      />
    </div>
  );
}
